import { useEffect, useRef } from 'react';
import { useLazyQuery } from '@apollo/client/react/hooks/useLazyQuery';
import { gql } from '@yi/core';

import { useAccessTier } from '~/src/common/subscription/useAccessTier';

import { sendPageView } from './sendPageView';

export type PageViewData = Parameters<typeof sendPageView>[0]['pageViewData'];

interface Args {
  pageViewData?: PageViewData;
}

/***
 * This hook addresses an issue where `currentUser` was being updated with each call to
 * `logConsumptionTracking`, resulting in a page view event being sent every minute.
 */
export const useSendPageViewAnalytics = ({ pageViewData }: Args) => {
  const lastKeyRef = useRef('');

  const [getCurrentUser, { data, loading: loadingCurrentUser, called }] = useLazyQuery<
    gql.typesClient.Query_CurrentUser_Base,
    {}
  >(gql.Query_CurrentUser_Base, {
    ssr: false,
    fetchPolicy: 'cache-and-network',
  });
  const currentUser = data?.CurrentUser ?? null;

  const { tier, loading: loadingTier } = useAccessTier();

  const loading = loadingCurrentUser || loadingTier;

  useEffect(() => {
    if (called) return;
    getCurrentUser();
  }, [getCurrentUser, called]);

  useEffect(() => {
    if (loading || !currentUser) return;

    const key = `${tier}|${currentUser?.id}|${pageViewData?.contentId}`;
    if (lastKeyRef.current === key) return; // prevent sending duplicate page views
    lastKeyRef.current = key;

    sendPageView({ currentUser, pageViewData, tier });
  }, [loading, currentUser, pageViewData, tier]);
};
